// ---------------------------------------------------------------------------
// Tokenii de design — sursă unică de adevăr.
//
// Tot ce ține de aspect (culori, raze, umbre, ritm, curbe de animație) se
// declară aici. Fișierul e citit din două locuri:
//
//   1. `theme.js`  — construiește tema MUI (butoane, câmpuri, tipografie)
//   2. `App.js`    — injectează `light`/`dark` ca variabile CSS pe `:root`
//
// Așa nu mai există două liste de culori care se pot desincroniza, iar orice
// `sx` poate scrie `var(--qf-ink)` fără să știe în ce schemă de culoare e.
//
// Schema urmează sistemul, prin `prefers-color-scheme`: pe un telefon pus pe
// întuneric site-ul e întunecat, fără niciun buton de comutare de întreținut.
// ---------------------------------------------------------------------------

// Curba de accelerare Apple: pornește lin, se oprește lin, fără elasticitate.
export const EASE = [0.28, 0.11, 0.32, 1];
export const EASE_CSS = 'cubic-bezier(.28,.11,.32,1)';

export const DUR = { fast: 0.22, base: 0.4, slow: 0.65 };

// Raze. `pill` e 980px, valoarea folosită de Apple pentru butoane-capsulă:
// orice înălțime ar avea butonul, capetele rămân semicercuri perfecte.
export const RADIUS = {
  sm: 10,
  md: 14,
  lg: 20,
  xl: 28,
  pill: 980,
};

export const FONT = {
  // Pe iPhone/Mac cade pe SF Pro, fontul de sistem — text nativ, zero descărcare.
  // Pe Android/Windows cade pe Inter (încărcat în index.html), care are aceleași
  // proporții neutre. Restul sunt plase de siguranță.
  sans: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Inter", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  // Cifrele din tabele și indicatori: lățime fixă, ca să nu joace coloanele.
  mono: 'ui-monospace, SFMono-Regular, "SF Mono", "Roboto Mono", Menlo, Consolas, monospace',
};

// Lățimea coloanei de conținut și aerul lateral. Aerul respectă zona sigură,
// ca pe iPhone în peisaj textul să nu intre sub colțurile rotunjite.
export const LAYOUT = {
  maxWidth: 1120,
  gutter: {
    xs: 'max(20px, env(safe-area-inset-left))',
    sm: 'max(28px, env(safe-area-inset-left))',
    lg: '40px',
  },
};

// Scara tipografică. Fiecare treaptă e `clamp(minim, fluid, maxim)`: nu există
// niciun prag la care textul sare, iar pe un telefon de 320px nimic nu depășește
// coloana. Tracking-ul se strânge pe măsură ce corpul crește — regula clasică
// de la Apple, altfel titlurile mari par rărite.
export const TYPE = {
  display: {
    fontSize: 'clamp(2.75rem, 9vw, 5rem)',
    lineHeight: 1.04,
    letterSpacing: '-0.035em',
    fontWeight: 700,
  },
  h1: {
    fontSize: 'clamp(2.125rem, 6vw, 3.5rem)',
    lineHeight: 1.08,
    letterSpacing: '-0.03em',
    fontWeight: 700,
  },
  h2: {
    fontSize: 'clamp(1.75rem, 4.2vw, 2.75rem)',
    lineHeight: 1.12,
    letterSpacing: '-0.026em',
    fontWeight: 700,
  },
  h3: {
    fontSize: 'clamp(1.1875rem, 2.4vw, 1.5rem)',
    lineHeight: 1.25,
    letterSpacing: '-0.018em',
    fontWeight: 600,
  },
  lede: {
    fontSize: 'clamp(1.0625rem, 1.9vw, 1.3125rem)',
    lineHeight: 1.5,
    letterSpacing: '-0.012em',
    fontWeight: 400,
  },
  body: {
    fontSize: '1.0625rem',   // 17px — corpul de text de pe iOS
    lineHeight: 1.55,
    letterSpacing: '-0.01em',
  },
  small: {
    fontSize: '0.9375rem',   // 15px
    lineHeight: 1.5,
    letterSpacing: '-0.006em',
  },
  caption: {
    fontSize: '0.8125rem',   // 13px
    lineHeight: 1.45,
    letterSpacing: '0',
  },
};

// Marca. Albastrul e cel din siglă; chihlimbarul rămâne accentul de date
// (reperul „azi", punctul paginii active, curba realizată).
const BRAND = {
  blue: '#0A6CF1',
  blueDark: '#4C9DFF',
  navy: '#0B2A5B',
  amber: '#F0A415',
  amberDark: '#FFB627',
};

// ---------------------------------------------------------------------------
// Schema deschisă. Gri neutru, fără tentă albastră — hârtia trebuie să fie
// hârtie, culoarea vine din conținut.
// ---------------------------------------------------------------------------
export const light = {
  '--qf-bg': '#FFFFFF',
  '--qf-bg-alt': '#F5F5F7',
  '--qf-card': '#FFFFFF',
  '--qf-card-2': '#F5F5F7',

  '--qf-ink': '#1D1D1F',
  '--qf-ink-2': '#4B4B51',
  // Griul lui Apple pentru text secundar e #86868B, care pe alb dă 3,5:1 —
  // sub pragul AA. Etichetele mono de 11-12px sunt exact textul care are
  // nevoie de contrast, deci treapta a treia e coborâtă până trece.
  '--qf-ink-3': '#6E6E73',

  '--qf-line': 'rgba(0, 0, 0, 0.09)',
  '--qf-line-2': 'rgba(0, 0, 0, 0.16)',

  // Materialul translucid. Tenta e aproape albă și destul de opacă (72%) ca
  // textul de deasupra să rămână lizibil peste orice trece pe dedesubt —
  // sticla nu are voie să coste contrast.
  '--qf-glass': 'rgba(255, 255, 255, 0.72)',
  '--qf-glass-strong': 'rgba(255, 255, 255, 0.86)',
  '--qf-glass-solid': '#FFFFFF',          // retragerea, unde `backdrop-filter` lipsește
  '--qf-glass-line': 'rgba(0, 0, 0, 0.08)',
  '--qf-glass-tint': 'rgba(0, 0, 0, 0.04)',

  '--qf-accent': BRAND.blue,
  '--qf-accent-hover': '#2C82FF',
  '--qf-accent-ink': '#FFFFFF',
  '--qf-accent-soft': 'rgba(10, 108, 241, 0.10)',
  '--qf-amber': '#C97F05',                 // închis pe alb, ca să treacă AA
  '--qf-amber-mark': BRAND.amber,          // nemodificat acolo unde e doar semn
  '--qf-navy': BRAND.navy,

  '--qf-danger': '#C1272D',
  '--qf-danger-soft': 'rgba(193, 39, 45, 0.08)',
  '--qf-ok': '#1B7F3B',

  // Lumina de sub hero și caroiajul de planșă. Culorile nu pot fi derivate din
  // accent cu `var()` (nu există alfa peste o variabilă), deci stau separat.
  '--qf-hero-1': 'rgba(10, 108, 241, 0.13)',
  '--qf-hero-2': 'rgba(240, 164, 21, 0.11)',
  '--qf-hero-grid': 'rgba(0, 0, 0, 0.045)',

  // Umbre foarte late și foarte slabe: se citesc ca lumină, nu ca contur.
  '--qf-shadow-sm': '0 1px 2px rgba(0,0,0,.04), 0 4px 12px rgba(0,0,0,.05)',
  '--qf-shadow-md': '0 2px 6px rgba(0,0,0,.05), 0 12px 32px rgba(0,0,0,.07)',
  '--qf-shadow-lg': '0 4px 12px rgba(0,0,0,.06), 0 24px 60px rgba(0,0,0,.10)',

  // Panoul închis (banda de final, hero-ul de pe Acasă). Rămâne închis în
  // ambele scheme; se schimbă doar cât de adânc e.
  '--qf-deep': '#0B2A5B',
  '--qf-deep-2': '#0E356F',
  '--qf-deep-ink': '#FFFFFF',
  '--qf-deep-ink-2': 'rgba(255, 255, 255, 0.72)',
  '--qf-deep-line': 'rgba(255, 255, 255, 0.16)',
  '--qf-deep-glass': 'rgba(255, 255, 255, 0.08)',

  // Bara de navigație.
  '--qf-nav-bg': 'rgba(255, 255, 255, 0.72)',
  '--qf-nav-bg-scrolled': 'rgba(255, 255, 255, 0.86)',
  '--qf-nav-solid': 'rgba(255, 255, 255, 0.97)',
  '--qf-nav-line': 'rgba(0, 0, 0, 0.10)',
  '--qf-nav-ink': '#1D1D1F',
  '--qf-nav-ink-2': 'rgba(29, 29, 31, 0.68)',
  '--qf-nav-hover': 'rgba(0, 0, 0, 0.05)',
  '--qf-nav-shadow': '0 1px 2px rgba(0,0,0,.04), 0 8px 28px rgba(0,0,0,.08)',
};

// ---------------------------------------------------------------------------
// Schema închisă. Fundal negru (pe OLED-uri economisește baterie și dă un
// contrast pe care griul nu-l atinge), suprafețele urcă în trepte de gri.
// ---------------------------------------------------------------------------
export const dark = {
  '--qf-bg': '#000000',
  '--qf-bg-alt': '#0B0B0D',
  '--qf-card': '#141416',
  '--qf-card-2': '#1C1C1F',

  '--qf-ink': '#F5F5F7',
  '--qf-ink-2': 'rgba(235, 235, 245, 0.72)',
  '--qf-ink-3': 'rgba(235, 235, 245, 0.58)',

  '--qf-line': 'rgba(255, 255, 255, 0.12)',
  '--qf-line-2': 'rgba(255, 255, 255, 0.22)',

  '--qf-glass': 'rgba(28, 28, 30, 0.72)',
  '--qf-glass-strong': 'rgba(28, 28, 30, 0.88)',
  '--qf-glass-solid': '#1C1C1E',
  '--qf-glass-line': 'rgba(255, 255, 255, 0.14)',
  '--qf-glass-tint': 'rgba(255, 255, 255, 0.06)',

  '--qf-accent': BRAND.blueDark,
  '--qf-accent-hover': '#6DB0FF',
  '--qf-accent-ink': '#04204A',
  '--qf-accent-soft': 'rgba(76, 157, 255, 0.16)',
  '--qf-amber': BRAND.amberDark,
  '--qf-amber-mark': BRAND.amberDark,
  '--qf-navy': '#12315F',

  '--qf-danger': '#FF8A80',
  '--qf-danger-soft': 'rgba(255, 138, 128, 0.12)',
  '--qf-ok': '#5CD68A',

  '--qf-hero-1': 'rgba(76, 157, 255, 0.20)',
  '--qf-hero-2': 'rgba(255, 182, 39, 0.10)',
  '--qf-hero-grid': 'rgba(255, 255, 255, 0.055)',

  // Pe negru, umbra nu se vede; adâncimea vine din muchie și din treapta de gri.
  '--qf-shadow-sm': '0 1px 2px rgba(0,0,0,.6)',
  '--qf-shadow-md': '0 8px 28px rgba(0,0,0,.7)',
  '--qf-shadow-lg': '0 20px 56px rgba(0,0,0,.8)',

  '--qf-deep': '#0A1A33',
  '--qf-deep-2': '#0D2549',
  '--qf-deep-ink': '#F5F5F7',
  '--qf-deep-ink-2': 'rgba(235, 235, 245, 0.68)',
  '--qf-deep-line': 'rgba(255, 255, 255, 0.14)',
  '--qf-deep-glass': 'rgba(255, 255, 255, 0.07)',

  '--qf-nav-bg': 'rgba(22, 22, 23, 0.72)',
  '--qf-nav-bg-scrolled': 'rgba(22, 22, 23, 0.88)',
  '--qf-nav-solid': 'rgba(22, 22, 23, 0.97)',
  '--qf-nav-line': 'rgba(255, 255, 255, 0.14)',
  '--qf-nav-ink': '#F5F5F7',
  '--qf-nav-ink-2': 'rgba(245, 245, 247, 0.68)',
  '--qf-nav-hover': 'rgba(255, 255, 255, 0.10)',
  '--qf-nav-shadow': '0 8px 30px rgba(0, 0, 0, 0.55)',
};

// Culorile brute, pentru locurile unde MUI are nevoie de o valoare pe care o
// poate descompune (calculează singur stările de hover, deci nu acceptă `var()`).
export const RAW = { ...BRAND, light, dark };

// ---------------------------------------------------------------------------
// Rețete de suprafață, folosite din `sx`.
// ---------------------------------------------------------------------------

// Sticla. `saturate(180%)` înainte de `blur` e ceea ce dă materialului Apple
// culoarea aceea vie în loc de un gri spălăcit. Retragerea (`@supports not`)
// nu e opțională: fără ea, pe browserele fără `backdrop-filter` textul rămâne
// peste un fundal aproape transparent, adică ilizibil.
export const glass = (strong = false) => ({
  backgroundColor: strong ? 'var(--qf-glass-strong)' : 'var(--qf-glass)',
  backdropFilter: 'saturate(180%) blur(24px)',
  WebkitBackdropFilter: 'saturate(180%) blur(24px)',
  border: '1px solid var(--qf-glass-line)',
  '@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))': {
    backgroundColor: 'var(--qf-glass-solid)',
  },
});

// Cartonul opac: aceeași geometrie, fără filtru — pentru liste lungi, unde un
// `backdrop-filter` pe toată înălțimea paginii costă cadre pe telefon.
export const card = {
  backgroundColor: 'var(--qf-card)',
  border: '1px solid var(--qf-line)',
  borderRadius: `${RADIUS.xl}px`,
  boxShadow: 'var(--qf-shadow-sm)',
};

// Ridicarea la survol, doar acolo unde există un cursor. Pe touch, `:hover`
// rămâne lipit după atingere și cardul pare blocat în starea apăsată.
export const hoverLift = {
  transition: `transform ${DUR.base}s ${EASE_CSS}, box-shadow ${DUR.base}s ${EASE_CSS}, border-color ${DUR.base}s ${EASE_CSS}`,
  '@media (hover: hover)': {
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: 'var(--qf-shadow-md)',
      borderColor: 'var(--qf-line-2)',
    },
  },
};

// Inelul de focus. Unul singur, vizibil pe orice fundal, doar la navigarea cu
// tastatura (`:focus-visible`, nu `:focus`).
export const focusRing = {
  '&:focus-visible': {
    outline: '2px solid var(--qf-accent)',
    outlineOffset: '3px',
  },
};

// Eticheta tehnică de deasupra titlurilor.
export const eyebrow = {
  fontFamily: FONT.mono,
  fontSize: 12,
  fontWeight: 500,
  letterSpacing: '.12em',
  textTransform: 'uppercase',
  color: 'var(--qf-accent)',
};

// Distanța dintre bara fixă și primul rând de conținut (calculată în index.css).
export const PAGE_TOP = 'var(--page-top)';

// Ritmul vertical dintre secțiuni. O singură pereche de valori peste tot.
export const SECTION_Y = { xs: 8, md: 14 };

// `100dvh` cu retragere la `100vh`: pe iOS, `vh` include bara de adrese care
// dispare la scroll, ceea ce producea un salt de layout.
export const fullHeight = {
  minHeight: '100vh',
  '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
};

// Rădăcina fiecărei pagini: crește exact cât spațiul rămas între bară și footer.
export const fillsMain = { flex: '1 0 auto' };
